import { avatarSrc } from "@/lib/defaults";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { denominationLabel } from "@/lib/denominations";
import { AffinityRing } from "@/components/AffinityRing";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const CommonPointsWidget = () => {
  const { t, user, config } = useApp();
  const [matches, setMatches] = useState([]);
  const count = Math.min(6, Math.max(1, Number(config?.settings?.algorithms?.suggestions?.sidebar_count) || 2));

  useEffect(() => {
    if (!user) return;
    return whenFeedReady(() => api.get("/matching/candidates").then(({ data }) => {
      // Rotate: pick N at random among candidates so suggestions change on each refresh —
      // but members of MY city (affinity.city_match) always come first.
      const shuffle = (arr) => {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      };
      const all = data.candidates || [];
      const near = shuffle(all.filter((c) => c.affinity?.city_match));
      const far = shuffle(all.filter((c) => !c.affinity?.city_match));
      setMatches([...near, ...far].slice(0, count));
    }).catch(() => {}));
  }, [user, count]);

  return (
    <div className="yalola-card p-5" data-testid="common-points-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-primary)" }}>
          <HeartHandshake size={14} /> {t("matching.title", "Points en commun")}
        </div>
        <Link to="/matching" className="text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="matching-see-all">
          {t("common.see_all", "Voir tout")}
        </Link>
      </div>
      {matches.length === 0 ? (
        <div className="mt-3">
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
            {t("matching.empty_sidebar", "Complète ton profil pour trouver des affinités.")}
          </p>
          {user && (!user.gender || !user.city || !user.denomination || (user.interests || []).length < 3) && (
            <Link
              to={`/profile/${user.id}`}
              className="inline-block mt-2 text-sm font-semibold hover:underline"
              style={{ color: "var(--yalola-primary)" }}
              data-testid="complete-profile-link"
            >
              {t("matching.complete_profile", "Compléter mon profil")} →
            </Link>
          )}
        </div>
      ) : (
        <ul className="mt-3 space-y-3">
          {matches.map(m => (
            <li key={m.id} className="flex items-center gap-3">
              <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(m)} /><AvatarFallback>{m.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>
                  {m.affinity?.city_match && <MapPin size={11} className="inline-block align-[-1px] mr-0.5" style={{ color: "var(--yalola-primary)" }} data-testid={`common-points-same-city-${m.id}`} />}
                  {m.city}{m.denomination ? ` · ${denominationLabel(t, m.denomination)}` : ""}
                </div>
              </div>
              <AffinityRing value={m.affinity?.score || 0} size={40} strokeWidth={4} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
